import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, User, Mail, Lock, Globe, Languages, Target, Bell, Volume2,
  Crown, CreditCard, Sparkles, Receipt, Shield, HelpCircle, FileText, LogOut, Trash2, Plus,
  Loader2, Eye, EyeOff, Check } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { UI_LANGS } from "@/i18n";

const Row = ({ icon: Icon, label, value, tint = "#2563EB", onClick, testid, danger, right }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-slate-50 transition-colors">
    <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: danger ? "#FEE2E2" : `${tint}16`, color: danger ? "#EF4444" : tint }}><Icon size={18} /></span>
    <span className={`flex-1 text-left font-semibold ${danger ? "text-[#EF4444]" : "text-[#0F172A]"}`}>{label}</span>
    {value && <span className="text-sm text-slate-400 truncate max-w-[130px]">{value}</span>}
    {right !== undefined ? right : (!danger && <ChevronRight size={18} className="text-slate-300" />)}
  </button>
);

const Toggle = ({ on, onChange, testid }) => (
  <span onClick={(e) => { e.stopPropagation(); onChange(!on); }} data-testid={testid}
    className={`w-11 h-6 rounded-full p-0.5 transition-colors cursor-pointer ${on ? "bg-[#2563EB]" : "bg-slate-300"}`}>
    <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : ""}`} />
  </span>
);

const Group = ({ title, children }) => (
  <div className="mt-5">
    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2 px-1">{title}</p>
    <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">{children}</div>
  </div>
);

const Sheet = ({ open, onClose, title, subtitle, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={onClose}>
      <div className="w-full max-w-[460px] bg-white rounded-t-3xl p-5 pb-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-4" />
        <p className="font-heading text-lg font-extrabold text-[#0F172A]">{title}</p>
        {subtitle && <p className="text-slate-500 text-sm mt-1 mb-3">{subtitle}</p>}
        <div className={subtitle ? "" : "mt-3"}>{children}</div>
      </div>
    </div>
  );
};

const PwField = ({ value, onChange, placeholder, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testid}
        className="w-full h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 pr-11 text-[#0F172A] outline-none focus:border-[#2563EB]" />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
};

const Input = ({ value, onChange, placeholder, testid, type = "text" }) => (
  <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[#0F172A] outline-none focus:border-[#2563EB]" />
);

const Btn = ({ children, onClick, busy, testid, danger }) => (
  <button onClick={onClick} disabled={busy} data-testid={testid}
    className={`w-full h-12 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-60 ${danger ? "bg-[#EF4444]" : "bg-[#2563EB]"}`}>
    {busy && <Loader2 size={18} className="animate-spin" />}{children}
  </button>
);

export default function Settings() {
  const nav = useNavigate();
  const { user, profile, premium, credits, logout, refresh, loadProfile } = useAuth();
  const { lang, setLang, t } = useLang();
  const [state, setState] = useState(null);
  const [langs, setLangs] = useState([]);
  const [sheet, setSheet] = useState(null); // 'goal'|'email'|'password'|'applang'|'learnlang'|'delete'|'info'
  const [pendingLang, setPendingLang] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notif, setNotif] = useState(profile?.notifications !== false);
  const [sound, setSound] = useState(profile?.sound !== false);

  // form fields
  const [curPw, setCurPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [delPw, setDelPw] = useState("");
  const [info, setInfo] = useState({ title: "", body: "" });

  useEffect(() => {
    client.get("/learn/state").then((r) => setState(r.data)).catch(() => {});
    client.get("/learn/languages").then((r) => setLangs(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (profile) { setNotif(profile.notifications !== false); setSound(profile.sound !== false); }
  }, [profile]);

  const closeSheet = () => { setSheet(null); setCurPw(""); setNewEmail(""); setNewPw(""); setConfirmPw(""); setDelPw(""); };

  const saveGoal = async (g) => {
    setState((s) => ({ ...s, daily_goal: g }));
    closeSheet();
    try { await client.post("/learn/daily-goal", { daily_goal: g }); toast.success("Daily goal updated"); } catch {}
  };

  const savePref = async (key, val) => {
    if (key === "notifications") setNotif(val); else setSound(val);
    try { await client.put("/profile", { [key]: val }); await loadProfile(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeEmail = async () => {
    if (!curPw || !newEmail) return toast.error("Fill in all fields");
    setBusy(true);
    try {
      const { data } = await client.post("/auth/change-password", { current_password: curPw, new_email: newEmail });
      if (data.token) localStorage.setItem("melletic_token", data.token);
      await refresh();
      toast.success("Email updated");
      closeSheet();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const changePassword = async () => {
    if (!curPw || !newPw) return toast.error("Fill in all fields");
    if (newPw.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const { data } = await client.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      if (data.token) localStorage.setItem("melletic_token", data.token);
      toast.success("Password updated");
      closeSheet();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const pickAppLang = async (code) => {
    setLang(code);
    closeSheet();
    try { await client.put("/profile", { app_lang: code }); await loadProfile(); toast.success(t("toast_lang_ui_updated")); } catch {}
  };

  const doSwitch = async (code) => {
    setPendingLang(null);
    closeSheet();
    try {
      await client.post("/learn/select-language", { code });
      const r = await client.get("/learn/state"); setState(r.data);
      await refresh();
      window.dispatchEvent(new CustomEvent("mel:progress-refresh"));
      toast.success(t("toast_lang_switched"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pickLearnLang = (code) => {
    if (code === state?.active_language) { closeSheet(); return; }
    const target = state?.languages?.find((l) => l.code === code);
    setPendingLang(target || { code, name: code?.toUpperCase(), flag: "🏳️" });
  };

  const deleteAccount = async () => {
    if (!delPw) return toast.error("Enter your password to confirm");
    setBusy(true);
    try {
      await client.post("/auth/delete-account", { current_password: delPw });
      toast.success("Account deleted");
      logout();
      nav("/login");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const openInfo = (title, body) => { setInfo({ title, body }); setSheet("info"); };
  const activeLang = state?.languages?.find((l) => l.code === state?.active_language);
  const appLangLabel = UI_LANGS.find((l) => l.code === lang)?.label || "English";

  return (
    <div className="min-h-screen w-full bg-[#0B1220] flex justify-center">
      <div className="relative w-full max-w-[460px] min-h-screen bg-[#F8FAFC] flex flex-col" style={{ paddingTop: "env(safe-area-inset-top,0px)" }}>
        <div className="flex items-center gap-3 px-4 pt-11 pb-3">
          <button onClick={() => nav("/profile")} data-testid="settings-back" className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center"><ChevronLeft size={22} className="text-[#0F172A]" /></button>
          <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("set_title")}</h1>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-10">
          <Group title={t("set_grp_account")}>
            <Row icon={User} label={t("set_profile")} onClick={() => nav("/profile")} testid="set-profile" />
            <Row icon={Mail} label={t("set_email")} value={user?.email} onClick={() => setSheet("email")} testid="set-email" />
            <Row icon={Lock} label={t("set_password")} onClick={() => setSheet("password")} testid="set-password" />
          </Group>
          <Group title={t("set_grp_learning")}>
            <Row icon={Plus} label={t("set_learning_languages")} onClick={() => nav("/choose-language")} testid="set-languages" />
            <Row icon={Globe} label={t("set_current_language")} value={activeLang ? `${activeLang.flag} ${activeLang.code?.toUpperCase()}` : "—"} onClick={() => setSheet("learnlang")} testid="set-current-language" />
            <Row icon={Languages} label={t("set_app_language")} value={appLangLabel} onClick={() => setSheet("applang")} testid="set-app-language" />
            <Row icon={Target} label={t("set_daily_goal")} value={state?.daily_goal ? `${state.daily_goal} ${t("common_min")}` : "—"} onClick={() => setSheet("goal")} testid="set-daily-goal" />
            <Row icon={Bell} label={t("set_notifications")} tint="#8B5CF6" onClick={() => savePref("notifications", !notif)} testid="set-notifications"
              right={<Toggle on={notif} onChange={(v) => savePref("notifications", v)} testid="toggle-notifications" />} />
            <Row icon={Volume2} label={t("set_sound")} tint="#8B5CF6" onClick={() => savePref("sound", !sound)} testid="set-sound"
              right={<Toggle on={sound} onChange={(v) => savePref("sound", v)} testid="toggle-sound" />} />
          </Group>
          {user?.role === "admin" && (
            <Group title={t("set_grp_admin")}>
              <Row icon={Shield} tint="#2563EB" label={t("set_admin")} value={t("set_admin_only")} onClick={() => nav("/admin")} testid="set-admin" />
            </Group>
          )}
          <Group title={t("set_grp_billing")}>            <Row icon={Crown} tint="#F59E0B" label={premium ? t("set_pro_active") : t("set_pro_go")} value={premium ? t("common_active") : t("set_free")} onClick={() => nav("/upgrade")} testid="set-pro" />
            <Row icon={CreditCard} tint="#F59E0B" label={premium ? t("set_manage_sub") : t("set_upgrade_pro")} onClick={() => nav("/upgrade")} testid="set-subscription" />
            <Row icon={Receipt} label={t("set_billing")} onClick={() => nav("/upgrade")} testid="set-billing" />
          </Group>
          <Group title={t("set_grp_privacy")}>
            <Row icon={Shield} tint="#22C55E" label={t("set_privacy")} onClick={() => openInfo(t("set_privacy"), t("info_privacy"))} testid="set-privacy" />
            <Row icon={HelpCircle} tint="#22C55E" label={t("set_support")} onClick={() => openInfo(t("set_support"), t("info_support"))} testid="set-support" />
            <Row icon={FileText} tint="#22C55E" label={t("set_terms")} onClick={() => openInfo(t("set_terms"), t("info_terms"))} testid="set-terms" />
          </Group>
          <Group title={t("set_grp_actions")}>
            <Row icon={LogOut} danger label={t("set_logout")} onClick={() => { logout(); nav("/login"); }} testid="set-logout" />
          </Group>
          <div className="mt-8 pt-2 border-t border-slate-200/70">
            <Group title={t("set_grp_danger")}>
              <Row icon={Trash2} danger label={t("set_delete")} value={t("set_delete_hint")} onClick={() => setSheet("delete")} testid="set-delete" />
            </Group>
          </div>
        </div>

        <Sheet open={sheet === "goal"} onClose={closeSheet} title={t("sheet_daily_goal")} subtitle={t("sheet_daily_goal_sub")} testid="daily-goal-modal">
          <div className="grid grid-cols-5 gap-2">
            {[5, 10, 15, 20, 30].map((g) => (
              <button key={g} onClick={() => saveGoal(g)} data-testid={`goal-${g}`}
                className={`py-3 rounded-2xl font-bold ${state?.daily_goal === g ? "bg-[#2563EB] text-white" : "bg-slate-100 text-[#0F172A]"}`}>{g}</button>
            ))}
          </div>
        </Sheet>

        <Sheet open={sheet === "email"} onClose={closeSheet} title={t("sheet_change_email")} subtitle={t("sheet_current", { v: user?.email || "" })} testid="email-modal">
          <div className="space-y-3">
            <Input value={newEmail} onChange={setNewEmail} placeholder={t("sheet_new_email")} testid="email-new" type="email" />
            <PwField value={curPw} onChange={setCurPw} placeholder={t("sheet_current_pw")} testid="email-current-pw" />
            <Btn onClick={changeEmail} busy={busy} testid="email-save">{t("sheet_update_email")}</Btn>
          </div>
        </Sheet>

        <Sheet open={sheet === "password"} onClose={closeSheet} title={t("sheet_change_pw")} testid="password-modal">
          <div className="space-y-3">
            <PwField value={curPw} onChange={setCurPw} placeholder={t("sheet_current_pw")} testid="pw-current" />
            <PwField value={newPw} onChange={setNewPw} placeholder={t("sheet_new_pw")} testid="pw-new" />
            <PwField value={confirmPw} onChange={setConfirmPw} placeholder={t("sheet_confirm_pw")} testid="pw-confirm" />
            <Btn onClick={changePassword} busy={busy} testid="pw-save">{t("sheet_update_pw")}</Btn>
          </div>
        </Sheet>

        <Sheet open={sheet === "applang"} onClose={closeSheet} title={t("sheet_app_lang")} testid="applang-modal">
          <div className="space-y-2">
            {UI_LANGS.map((l) => (
              <button key={l.code} onClick={() => pickAppLang(l.code)} data-testid={`applang-${l.code}`}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100">
                <span className="text-xl">{l.flag}</span>
                <span className="flex-1 text-left font-semibold text-[#0F172A]">{l.label}</span>
                {lang === l.code && <Check size={18} className="text-[#2563EB]" />}
              </button>
            ))}
          </div>
        </Sheet>

        <Sheet open={sheet === "learnlang"} onClose={closeSheet} title={t("sheet_current_lang")} subtitle={t("sheet_current_lang_sub")} testid="learnlang-modal">
          <div className="space-y-2">
            {(state?.languages || []).map((l) => (
              <button key={l.code} onClick={() => pickLearnLang(l.code)} data-testid={`learnlang-${l.code}`}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100">
                <span className="text-xl">{l.flag}</span>
                <span className="flex-1 text-left font-semibold text-[#0F172A]">{t(`lang_${l.code}`)} · {l.level}</span>
                {state?.active_language === l.code && <Check size={18} className="text-[#2563EB]" />}
              </button>
            ))}
            <button onClick={() => { closeSheet(); nav("/choose-language"); }} data-testid="learnlang-add"
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-slate-300 text-[#2563EB] font-semibold">
              <Plus size={18} /> {t("sheet_add_language")}
            </button>
          </div>
        </Sheet>

        {pendingLang && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-end justify-center" onClick={() => setPendingLang(null)}>
            <div className="w-full max-w-[460px] bg-white rounded-t-3xl p-5 pb-8" onClick={(e) => e.stopPropagation()} data-testid="switch-lang-modal">
              <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-4" />
              <p className="font-heading text-lg font-extrabold text-[#0F172A]">{t("switch_lang_title")}</p>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">{t("switch_lang_body", { cur: activeLang ? t(`lang_${activeLang.code}`) : "—", tgt: t(`lang_${pendingLang.code}`) })}</p>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setPendingLang(null)} data-testid="switch-lang-cancel"
                  className="flex-1 h-12 rounded-xl bg-slate-100 text-[#0F172A] font-bold">{t("common_cancel")}</button>
                <button onClick={() => doSwitch(pendingLang.code)} data-testid="switch-lang-confirm"
                  className="flex-1 h-12 rounded-xl bg-[#2563EB] text-white font-bold flex items-center justify-center gap-1.5"><span className="text-lg leading-none">{pendingLang.flag}</span> {t("switch_lang_confirm", { tgt: t(`lang_${pendingLang.code}`) })}</button>
              </div>
            </div>
          </div>
        )}


        <Sheet open={sheet === "info"} onClose={closeSheet} title={info.title} testid="info-modal">          <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{info.body}</p>
        </Sheet>

        <Sheet open={sheet === "delete"} onClose={closeSheet} title={t("sheet_delete_title")} subtitle={t("sheet_delete_sub")} testid="delete-modal">
          <div className="space-y-3">
            <PwField value={delPw} onChange={setDelPw} placeholder={t("sheet_delete_pw")} testid="delete-current-pw" />
            <Btn onClick={deleteAccount} busy={busy} testid="delete-confirm" danger>{t("sheet_delete_confirm")}</Btn>
          </div>
        </Sheet>
      </div>
    </div>
  );
}
